import EmptyState from '@Guidelines/Component/EmptyState';
import WorkflowRunCard from '@UI/Pages/WorkflowRuns/WorkflowRunCard';
import workflowRunFiltersSignal, {
	setWorkflowRunFilters,
} from '@UI/Pages/WorkflowRuns/Filters/Signals/workflowRunFiltersSignal';
import useEffectiveWorkflowRunFilters from '@UI/Pages/WorkflowRuns/Hooks/useEffectiveWorkflowRunFilters';
import type FacilityWorkflowRunsListView from '@UI/Store/WorkflowRuns/Views/FacilityWorkflowRunsListView';
import { css } from '@emotion/react';
import { SearchX, ShieldCheck } from 'lucide-react';
import * as React from 'react';
import {
	Button,
	DataView as WorkflowRunDataview,
	Message,
	type PaginatorPageChangeEvent,
} from '@verclam/ui-kit';
import type SubjectDirectoryListView from '@UI/Store/Subjects/Views/SubjectDirectoryListView';
import type BeneficiaryDirectoryListView from '@UI/Store/Beneficiaries/Views/BeneficiaryDirectoryListView';
import type FacilityWorkflowRunView from '@UI/Store/WorkflowRuns/Views/FacilityWorkflowRunView';
import { dataViewGridPt } from '@Guidelines/Theme/commonPassthrough';

const errorBannerStyle = css`
	display: flex;
	align-items: center;
	gap: 0.75rem;
	flex-wrap: wrap;
`;

type Props = {
	beneficiaryDirectory: BeneficiaryDirectoryListView | null;
	subjectDirectory: SubjectDirectoryListView | null;
	processingList: FacilityWorkflowRunsListView | null;
	processingListError: Error | null;
	isLoadingProcessingList: boolean;
	refreshProcessingList: () => void;
	isFetchingProcessingList: boolean;
};

/**
 * Renders every branch of the supervision screen's display. The page reads the list through a single
 * `useStoreQuery` whose `deps` are the debounced filters (300 ms): while a new question is pending,
 * `data` is `null` and the skeleton replaces the previous cards. A filter change landing during an
 * in-flight request is only emitted on the next refresh ("Actualiser" or the 5 s polling).
 * `queryKey` folds in a mount timestamp so every arrival on the screen asks a fresh question.
 */
const WorkflowRunsContent: React.FC<Props> = ({
	beneficiaryDirectory,
	subjectDirectory,
	processingList,
	isLoadingProcessingList,
	processingListError,
	refreshProcessingList,
	isFetchingProcessingList,
}) => {
	const filters = useEffectiveWorkflowRunFilters();

	const handlePageChange = React.useCallback((event: PaginatorPageChangeEvent) => {
		setWorkflowRunFilters(workflowRunFiltersSignal.peek().setPageAndRows(event.page, event.rows));
	}, []);

	const renderRunCard = React.useCallback(
		(run: FacilityWorkflowRunView) => {
			return (
				<WorkflowRunCard
					key={run.id}
					run={run}
					beneficiaries={beneficiaryDirectory}
					subjects={subjectDirectory}
				/>
			);
		},
		[beneficiaryDirectory, subjectDirectory],
	);

	const emptyMessage = React.useMemo(() => {
		const style = {
			height: '100%',
		};

		if (isLoadingProcessingList) {
			return <div style={style} />;
		}

		if (processingListError) {
			return (
				<Message
					severity="error"
					content={
						<div css={errorBannerStyle}>
							<span>Impossible de charger les traitements.</span>
							<Button
								type="button"
								label="Réessayer"
								severity="secondary"
								outlined
								onClick={refreshProcessingList}
							/>
						</div>
					}
				/>
			);
		}

		if (processingList?.list.length === 0) {
			if (filters.status === 'watch') {
				return (
					<EmptyState
						title="Aucun traitement à surveiller"
						description="Les traitements avancent normalement."
						icon={<ShieldCheck size={28} />}
					/>
				);
			}

			return (
				<EmptyState
					title="Aucun traitement ne correspond à ces filtres."
					icon={<SearchX size={28} />}
				/>
			);
		}

		return <EmptyState title={'Aucun traitement trouvé.'} icon={<SearchX size={28} />} />;
	}, [isLoadingProcessingList, processingList, processingListError, filters, refreshProcessingList]);

	return (
		<WorkflowRunDataview
			emptyMessage={emptyMessage}
			value={processingList?.list ?? []}
			layout="grid"
			dataKey="id"
			itemTemplate={renderRunCard}
			loading={isLoadingProcessingList || isFetchingProcessingList}
			pt={dataViewGridPt}
			lazy
			onPage={handlePageChange}
			paginator
			first={filters.page * filters.rows}
			rows={filters.rows}
			totalRecords={processingList?.totalRecords ?? 0}
			rowsPerPageOptions={[10, 25, 50, 100]}
		/>
	);
};

export default WorkflowRunsContent;
